// src/components/reader/LessonToast.jsx
//
// The completion moment (lesson-3b-feedback): a small bottom toast confirming a
// section is done and offering the next one in a tap.
//
// Deliberately modest. It sits ABOVE the mobile lesson bar, auto-dismisses after 4s,
// and only ever shows one at a time (the caller holds a single toast in state, so a
// second mark-complete replaces the first rather than stacking). Nothing is persisted.
//
// Portalled to document.body: the reader shell's .page-fade-in keeps a lingering
// transform, which would make `position: fixed` anchor to that full-height div instead
// of the viewport -- the same reason the drawers and the sections sheet portal.
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ReadingPrefsContext } from '../../pages/useReadingPrefs.js';
import { portalSkinClass } from '../../pages/readingPrefs.js';
import { IconCheck } from '../icons.jsx';
import './lesson.css';

export const TOAST_MS = 4000;

export default function LessonToast({ toast, onDismiss, onAction }) {
    const { colorMode } = React.useContext(ReadingPrefsContext);

    // Re-armed per toast id, so replacing a toast restarts its clock rather than
    // inheriting the previous one's remaining time.
    useEffect(() => {
        if (!toast) return undefined;
        const t = setTimeout(onDismiss, TOAST_MS);
        return () => clearTimeout(t);
    }, [toast, onDismiss]);

    if (!toast) return null;

    return createPortal(
        <div className={portalSkinClass(colorMode)}>
            <div className="btl-toast" role="status" aria-live="polite">
                <span className="w-5 h-5 rounded-full bg-book-accent text-book-pageDark flex items-center justify-center flex-shrink-0">
                    <IconCheck className="w-3 h-3" />
                </span>
                <span className="min-w-0 flex-grow font-display text-book-text text-sm font-semibold truncate">
                    {toast.message}
                </span>
                {toast.actionLabel && onAction && (
                    <button type="button" onClick={onAction}
                        className="flex-shrink-0 px-3 py-1.5 rounded-full bg-book-accent text-book-pageDark font-display text-xs font-semibold">
                        {toast.actionLabel}
                    </button>
                )}
                <button type="button" onClick={onDismiss} aria-label="Dismiss"
                    className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-book-muted hover:text-book-accent">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>,
        document.body,
    );
}
